import { SearchRegistry } from '../types';

export const integrationGuideSearchEntries: SearchRegistry = {
  // -- Integration Overview --
  '/integration-overview': {
    keywords: [
      'integration overview',
      'integration guide',
      'how to integrate',
      'getting started',
      'integration intro',
      'overview',
      'integration paths',
      'integration landing',
      'partner integration',
    ],
    questions: [
      'How do I integrate with ryd?',
      'Where is the integration overview?',
      'Which integration path fits my use case?',
    ],
  },

  // Network & Security moved to `security-and-compliance.ts` (ticket 2026-10-19).

  // -- rydpay APIs / Guides: General --
  '/integration-guide/rydpay-apis/getting-started': {
    keywords: [
      'rydpay',
      'ryd.pay',
      'getting started',
      'quickstart',
      'first integration',
      'hello world',
      'rydpay intro',
      'rydpay quickstart',
      'rydpay onboarding',
    ],
    questions: [
      'How do I start integrating with the rydpay APIs?',
      'Where is the rydpay getting-started guide?',
      'What is the first call I need to make against rydpay?',
    ],
  },
  '/integration-guide/rydpay-apis/integration-pathways': {
    keywords: [
      'integration pathways',
      'pathways',
      'fuelling integration',
      'ev charging integration',
      'car wash integration',
      'integration paths',
      'product paths',
      'fuelling pathway',
      'ev pathway',
      'wash pathway',
    ],
    questions: [
      'Which integration pathway should I pick — fuelling, EV charging, or car wash?',
      'How do the rydpay integration pathways differ?',
      'Can I integrate more than one pathway at the same time?',
    ],
  },
  '/integration-guide/rydpay-apis/key-concepts': {
    keywords: [
      'key concepts',
      'rydpay concepts',
      'place',
      'shop',
      'vending machine',
      'sku',
      'autoAcquire',
      'mobile authorize',
      'mobileAuthorize',
      'fastlane',
      'glossary',
      'core concepts',
      'pay at pump',
      'pop',
      'pay',
      'fuel grades',
      'pump',
      'lane',
      'pumps per station',
      'machines per station',
      'charge points per site',
      'evses per shop',
      'shops per place',
      'how many pumps',
      'how many products per pump',
      'station size',
      'fuelTypeId',
      'availableItems',
      'one product per order',
    ],
    questions: [
      'What are the key concepts of the rydpay APIs?',
      'What is the difference between Place, Shop and Vending Machine?',
      'What does autoAcquire do?',
      'What is fastLane in rydpay?',
      'How many vending machines does a station typically have?',
      'How many products does a vending machine offer?',
      'What is the maximum number of pumps or charge points I should support?',
      'Can one order contain multiple products?',
    ],
    faqs: [
      {
        q: 'What is the difference between a Place, a Shop and a Vending Machine in rydpay?',
        a: 'A Place is a physical location and it can contain MULTIPLE Shops. Each Shop has exactly one vending machine type — fuel, EV charging or car wash — so one place can offer several vending machine types, each as its own shop. A Vending Machine is the dispensing unit a transaction is bound to (a pump, a wash bay, a charge point), and each machine offers products (availableItems with itemCode, marketingName, unit). Most order calls take a `shopId` and a `vendingMachineId`. Note: ryd calls the dispensing unit a "vending machine" — for fuel this is a pump, for EV a charge point.',
      },
      {
        q: 'How many vending machines and products should I expect per station?',
        a: 'Measured across successful production orders Jan–Jun 2026 (~4,900 fuel stations, ~190 EV sites): fuel stations have a median of 6 vending machines (75th pct 8, 90th pct 10, max observed 30), a median of 3 fuel products per machine (90th pct 4, max 6) and a median of 4 fuel products per station (90th pct 6). EV sites are much smaller: median 2 charge points per site, 90th pct 3, each usually exposing 1 connector option (occasionally up to 3). But do NOT size your UI to the median only — there are production cases with one shop and 110 charge points, and one place with 250 shops. Your station and machine selection must paginate or scroll gracefully rather than assume a fixed maximum. A selector designed for ~10 machines covers 90% of fuel stations, and 5 product slots per machine covers effectively all cases. Staging simulators use 11 vending machines per station with 2–3 products each.',
      },
      {
        q: 'Can a single order contain more than one product?',
        a: 'No. Currently one product per order is supported. `fuelTypeId` must be sent whenever the shop returns `availableItems`, because it determines which nozzle is unlocked.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/api-fundamentals': {
    keywords: [
      'api fundamentals',
      'fundamentals',
      'http basics',
      'authentication',
      'pagination',
      'idempotency',
      'rate limits',
      'errors',
      'request format',
      'response format',
      'api versioning',
      'http headers',
      'auth header',
      'static api key',
      'x-txn-auth-token',
      'rydpay api key',
      'rydpay authentication',
      'bearer token',
      'json request',
      'json response',
      'request id',
    ],
    questions: [
      'What are the API fundamentals — auth, pagination, errors?',
      'How do rydpay APIs authenticate?',
      'Do rydpay APIs use the same authentication as Fleet APIs?',
      'How does idempotency work in rydpay?',
      'Which HTTP headers does rydpay require?',
      'How is pagination encoded?',
    ],
    faqs: [
      {
        q: 'Do rydpay APIs and Fleet APIs use the same authentication?',
        a: 'No. rydpay APIs use a static API key sent in the `x-txn-auth-token` HTTP header. Fleet APIs use OAuth2-based access tokens: Client Credentials for direct server-to-server integrations or JWT Bearer for delegated fleet access, followed by `Authorization: Bearer <access_token>` on Fleet API calls.',
      },
      {
        q: 'How do rydpay APIs authenticate requests?',
        a: 'rydpay APIs require a static API key in the `x-txn-auth-token` HTTP header. ryd provides separate keys for staging/test and production environments, and keys can be renewed on request.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/best-practices': {
    keywords: [
      'best practices',
      'recommendations',
      "do and don't",
      'integration best practices',
      'production checklist',
      'production readiness',
      'dos and donts',
      'go-live checklist',
      'production hardening',
      'idempotency tips',
      'retry strategy',
      'security radius',
      'geo-location check',
      'driver location',
      'proximity check',
      'GPS accuracy',
      'GPS freshness',
      'Haversine',
      '100 meters',
      'remote fueling prevention',
      'station distance',
      'authorization amount',
      'recommendedPrices',
      'priceAuthorizationLimitsAutomaticFlow',
      'min max amount',
      'quick select amounts',
      'amount steps',
      'overshoot',
      'concurrent authorizations',
      'multiple pumps at once',
      'live telemetry',
      'state of charge',
      'kw rate',
      'litres so far',
      'qr code discovery',
    ],
    questions: [
      'What are the rydpay integration best practices?',
      'What should I check before going to production?',
      'How should I implement retries against rydpay?',
      'What security checks does ryd perform on rydpay API calls?',
      'What security checks must I implement as a rydpay API partner?',
      'Does ryd block remote fueling automatically through the API?',
      'What GPS accuracy and freshness should I require before starting a session?',
      'Is a radius check needed for car wash ticket purchases?',
      'Are min and max prices technical limits?',
      'Can I use recommendedPrices to pre-populate quick actions?',
      'Can I define my own authorization amount steps?',
      'Can idle or parking fees exceed the pre-authorized amount?',
      'Is there a limit on concurrent active authorizations per user?',
      'Can I see live telemetry — litres so far, kW rate, state of charge?',
      'Can I discover stations via QR code?',
    ],
    faqs: [
      {
        q: 'What security checks does ryd perform on rydpay API calls?',
        a: "ryd enforces driver and fleet spending limits, payment-provider fraud checks, API rate limiting and auth-token validation server-side. It does not validate the driver's GPS location against the selected station for API calls — that check is only enforced in ryd's own Android, iOS and web apps (at a 100 m radius). Partners using rydpay APIs should implement the radius check themselves for fuel and EV-charge flows. Car wash purchases are exempt.",
      },
      {
        q: 'What security checks must a rydpay API partner implement?',
        a: 'A 100 m geo-location radius check for fuel and EV-charge sessions, GPS-accuracy validation (reject fixes worse than 50 m), GPS-freshness validation (reject fixes older than 30 s), server-side enforcement of the radius check (not client-only), session-ownership checks, and audit logging of every radius decision. Car wash flows do not require the radius check.',
      },
      {
        q: 'Can a driver buy a car wash ticket remotely through the rydpay APIs?',
        a: 'Yes. Car wash ticket purchases are intentionally allowed without a proximity check. Only fuel and EV-charge flows require the 100 m radius check.',
      },
      {
        q: 'Are min and max prices technical limits, and can I use recommendedPrices to pre-populate quick-select amounts?',
        a: 'The priceAuthorizationLimitsAutomaticFlow object is returned on the shop detail call and only for prepay vending machines (automaticFlow: true), so its presence is itself a prepay indicator. `min` and `max` are the allowed band for the pre-authorization amount, configured centrally by ryd PER COUNTRY — they are not technical limits of the individual machine; treat them as validation bounds. `recommendedPrices` are PROPOSALS, not requirements: they are the amount steps ryd uses in its own apps, maintained as static per-country configuration, and are not derived from prior usage or from the specific station. You are free to define your own amount steps, or to always authorize a fixed amount without offering a choice (common for EV). If you prefer not to design your own, recommendedPrices is a reasonable default set to render as quick-select buttons. Whatever steps you use, validate them against the min/max band and clamp defensively — the values are maintained per country and can in principle diverge from the recommended list.',
      },
      {
        q: 'Can idle or parking fees exceed the original authorization amount (overshoot)?',
        a: 'Yes. This is called overshoot and it can happen with BOTH products — neither EV chargers nor fuel pumps stop automatically at a monetary value. For EV, time-based tariff components such as parking or idle fees make it more likely for long-parked vehicles. Handling: once an order has passed PRODUCT_ACQUIRED, treat it as a successful order and let the customer leave — everything after that is post-processing and must not block the customer, and status.type stays SUCCESS even when the state history ends in PROBLEM. The order moves to the manual-resolution PROBLEM state and is processed by ryd payment operations. Within limits ryd covers the differential; for significant overages ryd or the partner may contact the customer for an additional payment. Mitigation: pre-authorize a sufficiently large amount up front, since a prepay authorization is only a reservation and the final payment is adjusted down to the amount actually dispensed. Reproduce it on staging with EV scenario TS 802 (€250 vs €40 pre-authorized) or fuelling TC 605.',
      },
      {
        q: 'Is there a limit on concurrent active authorizations per user? Could someone authorize on every vending machine at a station?',
        a: 'ryd does not cap the number of concurrently open authorizations per user for trusted API partners — you should cap concurrency (one, or a few) in your own app. Protections that do exist: on some cashier-system adapters — specifically those where a reservation is possible before the money is authorized — ryd prevents blocking multiple vending machines at once, though coverage is adapter-specific and depends on partner configuration; identical repeated non-GET requests within roughly 1–4 s are deduplicated (this stops double-taps but not distinct authorizations on different machines); a prepay reservation locks a single machine and expires after 60–120 s with the pre-authorization voided, so the scenario is self-limiting in duration; and driver spending and frequency limits plus per-machine min/max authorization bounds apply server-side. In practice multiple simultaneous sessions are rare — occasionally legitimate in EV charging, very rare in fuelling.',
      },
      {
        q: 'Can I see live vending machine telemetry such as litres dispensed so far, kW rate or state of charge?',
        a: 'Not today. The API exposes no live telemetry — there are no fields for current quantity, charging power or state of charge in the order schema. During an EV session you get the state flag PRODUCT_SUPPLYING, which is suitable only for a binary "charging in progress" indication. Quantity, unit and prices appear on the order only AFTER the final data arrives from the station or CPO. kW figures in station data are connector ratings (e.g. TYPE_2:22:AC), not live readings. ryd is working on reading car/charger telemetry from a third-party platform, but it is not yet integrated into the order lifecycle and not yet available to partners.',
      },
      {
        q: 'Does ryd support discovering stations or vending machines via QR code?',
        a: 'Not as a ryd-provided discovery mechanism. QR codes are used for car wash redemption — the purchased ticket code is shown and scanned at the terminal — not for discovery. Build discovery on GET /v4/places. If your app already knows which station the user is at (via GPS, your own QR code, or NFC), the mapping from your code to a shopId lives on your side. The upcoming ryd Connect "Skip the Map" capability targets this use case by letting you pass a shopId directly and skip the search step; it is in implementation and not yet available in production.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/order-state-management': {
    keywords: [
      'order state',
      'order state management',
      'state machine',
      'order lifecycle',
      'order statuses',
      'order status',
      'CREATED',
      'PAYMENT_PENDING',
      'PAYMENT_AUTHORIZED',
      'PRODUCT_PENDING',
      'PRODUCT_SUPPLYING',
      'PRODUCT_ACQUIRED',
      'READY_FOR_INVOICE',
      'COMPLETED',
      'COMPLETED_MANUALLY',
      'CANCELLED',
      'FAILED',
      'PROBLEM',
      'final states',
      'terminal states',
      'transitions',
      'order events',
      'state diagram',
      'state transition',
      'order finite state',
      'prepay',
      'postpay',
      'manual resolution',
      'overshoot',
      'stuck order',
      'exception state',
      'pump timeout',
      'pump read failure',
      'pump unlock confirmation',
      'authorize timeout',
      'invoice generation',
    ],
    questions: [
      'What states does an order go through?',
      'What are the final / terminal order states?',
      'How does order state management work in rydpay?',
      'When does an order transition to COMPLETED?',
      'Is the PROBLEM state always transient?',
      'Will every order eventually reach a final state like FAILED or COMPLETED?',
      'How do prepay and postpay state flows differ?',
      'What is the difference between order state and order status?',
    ],
    faqs: [
      {
        q: 'What does the order state machine look like in rydpay?',
        a: 'The prepay happy path is CREATED → PAYMENT_AUTHORIZED → PRODUCT_PENDING → PRODUCT_ACQUIRED → READY_FOR_INVOICE → COMPLETED (helper states PAYMENT_AUTHORIZATION_STARTED / PRODUCT_ACQUISITION_STARTED may appear in between). Postpay delivers the product first and finalizes payment later; EV charging adds PRODUCT_SUPPLYING. The four final states are COMPLETED, COMPLETED_MANUALLY, FAILED and CANCELLED.',
      },
      {
        q: 'Is the PROBLEM state always transient — will every order eventually reach FAILED or COMPLETED?',
        a: 'No. PROBLEM is transient in intent but not self-resolving: the automated state machine does not advance it on its own. It is a manual-resolution holding state (e.g. fuelling/EV overshoot) that ryd payment-operations must settle, typically into COMPLETED_MANUALLY or sometimes FAILED. The OpenAPI spec and FAQs treat PROBLEM as a possible final observed state, so a client should treat it as effectively terminal: stop polling and read the order error object plus stateHistory action codes. The four true terminal states an order rests in are: COMPLETED (successfully finalized, no further action), COMPLETED_MANUALLY (finalized by a manual admin adjustment — the typical landing state for a resolved PROBLEM; treat as final but log for support), FAILED (purchase failed; stop polling, user may retry with a new order) and CANCELLED (user/client cancelled; stop polling and surface the cancellation).',
      },
      {
        q: 'What triggers / causes the PROBLEM state?',
        a: 'PROBLEM means something went wrong during the order lifecycle that needs attention, but the order is not definitively failed yet (a "stuck"/exception state). Typical triggers: pump read failure or timeout (no response from the station), authorize confirmation timeout, no pump unlock confirmation, overshoot (fuelled/charged amount exceeds the pre-authorized amount), and invoice-generation issues after the product was already acquired. These require manual settlement by ryd payment-operations.',
      },
      {
        q: 'What is the difference between order state and order status?',
        a: 'Order state (stateHistory[]) is the technical lifecycle used for integration/polling logic. Order status (the status object: type SUCCESS/FAILURE, subtype, title, message) is the user-facing outcome used for UI messages.',
      },
      {
        q: 'How do I observe order state changes in real time?',
        a: 'Poll GET /v4/order/{orderUuid} (respecting pollIntervalMs) or subscribe to order webhooks. The stateHistory array records each transition with its state, message, timestamps and actionCode / actionCodeText.',
      },
      {
        q: 'Is PRODUCT_SUPPLYING emitted for fuelling and car wash, or only for EV charging?',
        a: 'PRODUCT_SUPPLYING means charging is in progress and exists for EV CHARGING ONLY — by specification. It is NOT used for fuelling or car wash. A fuelling or car wash order moves directly from PRODUCT_PENDING to PRODUCT_ACQUIRED, so never build your fuelling UX on PRODUCT_SUPPLYING. Production data (Jan–Jun 2026) confirms it: 0 of 1,245,609 acquired fuel orders and 0 of 2,872 acquired car wash orders carried the state, versus 97.1% of EV orders. There is also no live fuelling progress state and no live telemetry — use PRODUCT_PENDING (machine unlocked, ready to fuel) and then PRODUCT_ACQUIRED (session finished).',
      },
      {
        q: 'What does PRODUCT_PENDING mean, and what should I do after PRODUCT_ACQUIRED?',
        a: 'PRODUCT_PENDING means the vending machine is unlocked and reserved for the driver — it is the "ready to fuel/charge" signal, not a waiting state. Drive your "ready to fuel" / "plug in now" UI from it. PRODUCT_ACQUIRED means the session is finished and the customer can leave (for EV it is set when the customer unplugs). From PRODUCT_ACQUIRED onwards, treat the order as successful and let the customer leave: everything that follows is post-processing and organisational, and must not block the customer. status.type is SUCCESS even when the state history ends in PROBLEM. Note that the session result (PRODUCT_ACQUIRED) and the final amount (COMPLETED) are two separate events — for EV they can be minutes, hours, days or in some cases weeks apart.',
      },
      {
        q: 'What are the order timeouts, and can I cancel an order?',
        a: 'Timeouts and cancellation are documented on the Order Timeouts & Cancellation page. In short: the request timeout is always 60 seconds and you must never abort earlier; there is no single order timeout but six that apply in sequence (including a 60–120 s prepay reservation expiry that voids the pre-authorization); and POST /v4/order/{orderId}/cancel is best-effort and NOT guaranteed, so confirm the outcome via the order state or webhook. See /integration-guide/rydpay-apis/order-timeouts-and-cancellation.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/order-timeouts-and-cancellation': {
    keywords: [
      'order timeout',
      'order timeouts',
      'request timeout',
      '60 seconds',
      '60 second timeout',
      'client timeout',
      'reservation expiry',
      'reservation timeout',
      'pump release',
      'vending machine release',
      'release the pump',
      'unlock time',
      'unlock timing',
      'time to unlock',
      'sla',
      'cancel order',
      'cancellation',
      'cancel not guaranteed',
      'zero filling',
      'order expired',
      'inactivity timeout',
      'machine response timeout',
      'polling',
      'pollIntervalMs',
      'stop polling',
      'ongoing orders',
      'charging start time',
      'how long until charging starts',
    ],
    questions: [
      'What is the SLA for a vending machine to unlock after authorization?',
      'What is the request timeout for rydpay APIs?',
      'Should I use a shorter client-side timeout than 60 seconds?',
      'What happens if a user authorizes but never fuels or plugs in?',
      'Is there a timeout that releases the vending machine?',
      'How long does a prepay reservation last?',
      'Can I cancel an order? Is cancellation guaranteed?',
      'Can I stop an ongoing prepay fuelling?',
      'How long does it take for EV charging to start?',
      'How often should I poll an order?',
      'When should I stop polling?',
    ],
    faqs: [
      {
        q: 'What is the SLA for a vending machine to unlock after authorization, and what timeout should I code against?',
        a: 'There is no contractual SLA — actual timing depends on the station cashier system or the charge point operator. The only hard limit to code against is the request timeout, which is ALWAYS 60 seconds. Never abort a request from your side before that: ryd times out automatically after 60 seconds, and only then should your system stop waiting. Do not implement a shorter client-side timeout. Measured on production (Jan–Jun 2026): fuel pumps unlock in ~1 s median, ~6 s at the 90th percentile and 99.7% within 15 s; EV charge points are ready in ~2 s median. Those figures are measured behaviour, not an SLA, and must not be turned into a timeout.',
      },
      {
        q: 'What happens if a user authorizes payment but never picks up the nozzle or plugs in? Is there a timeout that releases the vending machine?',
        a: 'Yes. A prepay reservation expires after 60 to 120 seconds (depending on the cashier system) if unused, the reservation is released and the pre-authorization is voided so the money is returned. Several other timeouts also apply, in sequence: ~60 min of order inactivity leads to auto-cancellation ("Order Expired"); orders stuck in early created/payment states are cleaned up after ~10 min; an EV order that never reaches PAYMENT_PENDING is REJECTED after 5 min; an order waiting for the vending machine to respond is flagged PROBLEM after 7 min for reservations up to €200 or 20 min above €200 (configurable, depends on the chosen reservation limit); and a machine reserved but never used (PRODUCT_PENDING) is cleaned up after ~25 min. There is no single overall order timeout.',
      },
      {
        q: 'Can I cancel a rydpay order, and is cancellation guaranteed?',
        a: 'POST /v4/order/{orderId}/cancel works for orders in CREATED, or PRODUCT_PENDING for prepay/automatic orders. Cancellation is NOT guaranteed — it depends on the station and its cashier system. Treat it as a best-effort request and always confirm the outcome by polling the order state or waiting for the webhook rather than assuming success. An ongoing prepay fuelling cannot be cancelled via API; it can only be ended by hanging the nozzle back ("zero filling"). Once an order has passed PRODUCT_ACQUIRED, do not cancel — treat it as successful and let the customer leave, because everything after that milestone is post-processing.',
      },
      {
        q: 'How long does EV charging take to start after the charge point is ready?',
        a: 'Measured on production (Jan–Jun 2026, n = 7,354) for PRODUCT_PENDING → PRODUCT_SUPPLYING: median ~7 s, 75th percentile ~29 s, 90th ~59 s, 95th ~99 s. 76% of sessions start within 30 seconds, ~94% within 2 minutes, and only ~2% take longer than 5 minutes. This interval depends on the vehicle and the charge point, not on ryd. If the charge point never responds, the order is flagged PROBLEM after the machine-response timeout, so a session never hangs indefinitely.',
      },
      {
        q: 'How often should I poll an order, and when should I stop?',
        a: 'Use the pollIntervalMs hint returned on GET /v4/order/{orderId} instead of a hard-coded interval; GET /v4/ongoing-orders lists all non-final orders. For EV prefer webhooks over polling, because sessions last hours. Stop polling at COMPLETED, COMPLETED_MANUALLY, FAILED or CANCELLED, and treat PROBLEM as effectively terminal. For an order the customer never touched, stop after 5–10 minutes and show an appropriate message — the order-level timeouts will resolve it without you calling /cancel. Never build partner-facing logic on READY_FOR_INVOICE, which is an internal step.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/places-filtering-guide': {
    keywords: [
      'places filtering',
      'places filter',
      'station filtering',
      'poi filter',
      'place filter',
      'available stations',
      'eligible stations',
      'station eligibility',
      'place capability',
      'capability filter',
      'product capability',
      'shop filter',
      'places endpoint',
      'v4/places',
      'pois endpoint',
      'v4/pois',
      'legacy pois',
      'station discovery',
      'discover stations',
      'find stations',
      'isRydSupported',
      'isRydPay',
      'radiusM',
      'nearby stations',
    ],
    questions: [
      'How do I filter places by capability?',
      'Where is the places-filtering guide?',
      'How do I list only stations that support EV charging?',
      'How do I scope the place list to my country / partner?',
      'Which endpoint should I use to discover stations?',
      'Should I use /v4/pois or /v4/places?',
      'How do I find stations near the user?',
    ],
    faqs: [
      {
        q: 'Which endpoint should a new integration use to discover stations — /v4/places or /v4/pois?',
        a: 'Use GET /v4/places. The older /v4/pois endpoint is LEGACY: it still responds, but it is not the correct endpoint for new integrations and should not be used. The most common query is by radius around the user, where latitude, longitude and radiusM are required, plus isRydSupported=true to restrict results to locations that support ryd. Important parameter rename: on /v4/places the ryd-support filter is `isRydSupported`. `isRydPay` is the legacy /v4/pois flag and is rejected as a validation error on /v4/places. You can also query by country with address[countryCode] and page with pagination[limit] / pagination[offset], and narrow results by vendingMachinesTypes[] or productGroups[] (multiple values of the same filter are combined with OR). Example: GET /v4/places?latitude=53.5774664&longitude=13.26917512&radiusM=5000&isRydSupported=true. Shop details then come from GET /v4/shops/{shopId}.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/webhooks': {
    keywords: [
      'webhooks',
      'rydpay webhooks',
      'webhook setup',
      'webhook signature',
      'event webhooks',
      'callbacks',
      'event subscription',
      'webhook security',
      'webhook signing secret',
      'webhook retries',
      'webhook idempotency',
      'webhook timeouts',
      'webhook payload',
      'order events',
    ],
    questions: [
      'How do I configure rydpay webhooks?',
      'How do I verify a webhook signature?',
      'Which events does rydpay send via webhooks?',
      'How does ryd retry failed webhooks?',
    ],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation': {
    keywords: [
      'testing',
      'simulation',
      'test environment',
      'sandbox',
      'simulated stations',
      'test scenarios',
      'integration tests',
      'qa',
      'staging environment',
      'test fixtures',
      'qa walkthrough',
    ],
    questions: [
      'How do I test my rydpay integration?',
      'Where can I find simulated stations and test scenarios?',
      'How do I run an end-to-end fuelling test in staging?',
    ],
  },

  // -- rydpay APIs / Guides: Fuelling --
  '/integration-guide/rydpay-apis/guides-fuelling/prepay-and-postpay': {
    keywords: [
      'prepay',
      'postpay',
      'prepay vs postpay',
      'fuelling prepay',
      'fuelling postpay',
      'prepayment',
      'postpayment',
      'authorization fuelling',
      'capture fuelling',
      'reservation amount',
      'pre-authorisation',
      'post-pay',
      'pay before pump',
      'pay after pump',
      'automaticFlow',
      'automaticPump',
      'prepay indicator',
      'postpay indicator',
      'mixed pump modes',
      'is there a standard',
      'day night mode',
    ],
    questions: [
      'What is the difference between prepay and postpay fuelling?',
      'Should I integrate prepay or postpay first?',
      'When does the authorization happen for prepay?',
      'Which countries support postpay?',
      'Is there an indicator telling me whether a station is prepay or postpay?',
      'Can one station have both prepay and postpay vending machines?',
      'What is the difference between automaticFlow and automaticPump?',
    ],
    faqs: [
      {
        q: 'What is the difference between prepay and postpay fuelling?',
        a: 'Prepay reserves an amount before pumping and captures the actual fuelled amount afterwards. Postpay first allows pumping and captures the price after the fact. Country and brand availability differ — see the Prepay vs Postpay page for the country-by-country matrix.',
      },
      {
        q: 'Is there a standard, or an indicator that tells me whether a station works prepay or postpay?',
        a: 'Both flows exist and there is an explicit indicator: the `automaticFlow` attribute, returned PER VENDING MACHINE on the shop detail call GET /v4/shops/{shopId}. automaticFlow: true means prepay (authorize first, then the machine unlocks; taking the nozzle before authorization does not work; afterwards payment is adjusted down to the amount actually dispensed). automaticFlow: false means postpay (the machine can already be unlocked by the station; the user fuels first and then pays the known amount — both "app first, then fuel" and "fuel first, then pay" are valid postpay experiences). `automaticFlow` is the successor of the deprecated `automaticPump`; use automaticFlow in new integrations. Also note that priceAuthorizationLimitsAutomaticFlow is only returned when automaticFlow is true, so its presence is another prepay indicator.',
      },
      {
        q: 'Can one station have both prepay and postpay vending machines, and can the mode change?',
        a: 'Yes to both. The mode is machine-specific, NOT station-wide — do not assume one mode for all vending machines at a site. Modes can also change over time, for example postpay during staffed hours and prepay at night or on weekends. Always evaluate the current automaticFlow value; never cache it and never infer it from the product category. Country patterns are orientation only: Germany and Poland are largely postpay, Spain and Portugal largely prepay. On-site assistance cannot be assumed — prepay exists precisely for unstaffed hours — which is why the app flow must be driven by automaticFlow. Set autoAcquire: true on order creation for both modes; you then never need the deprecated /acquire call and simply follow the order to a terminal state via polling or webhooks.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/guides-fuelling/fuelling-client-flow': {
    keywords: [
      'fuelling client flow',
      'client flow',
      'fuelling sequence diagram',
      'client integration',
      'fuelling flow',
      'fuelling steps',
      'pump selection',
      'reserve pump',
      'authorize pump',
      'release pump',
      'fuel grade selection',
    ],
    questions: [
      'What is the client flow for fuelling?',
      'Which API calls do I need to make in order for a fuelling transaction?',
      'How does pump selection work?',
    ],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation/fuelling-test-scenarios': {
    keywords: [
      'fuelling test scenarios',
      'test scenarios fuelling',
      'fuelling sandbox tests',
      'test cases fuelling',
      'qa scenarios',
      'fuelling qa',
      'fuelling success scenario',
      'fuelling failure scenario',
      'fuelling timeout scenario',
    ],
    questions: [
      'Which test scenarios should I run for fuelling?',
      'Where are the canonical fuelling QA test cases?',
      'How do I simulate a failed fuelling?',
    ],
  },
  '/integration-guide/rydpay-apis/guides-fuelling/fuelling-postman-collection-viewer': {
    keywords: [
      'postman',
      'postman collection',
      'fuelling postman',
      'collection viewer',
      'postman viewer',
      'api requests fuelling',
      'postman download',
      'rest client',
    ],
    questions: [
      'Is there a Postman collection for fuelling?',
      'Where do I import the fuelling Postman collection?',
    ],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation/fuelling-simulated-stations': {
    keywords: [
      'simulated stations fuelling',
      'fuelling simulated stations',
      'sandbox stations',
      'test stations',
      'fake stations',
      'simulator',
      'simulated pump',
      'simulated forecourt',
    ],
    questions: [
      'Which simulated stations can I use for fuelling tests?',
      'Where is the list of fuelling sandbox stations?',
    ],
  },

  // -- rydpay APIs / Guides: Car Wash --
  '/integration-guide/rydpay-apis/testing-and-simulation/car-wash-test-scenarios': {
    keywords: [
      'car wash test scenarios',
      'car wash testing',
      'wash sandbox tests',
      'wash qa scenarios',
      'wash test cases',
      'autowäsche test',
    ],
    questions: [
      'Which test scenarios should I run for car wash?',
      'Where are the car-wash test cases?',
    ],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation/car-wash-simulated-stations': {
    keywords: [
      'car wash simulated stations',
      'wash simulated stations',
      'wash sandbox stations',
      'wash test stations',
      'car wash sandbox',
    ],
    questions: ['Which simulated stations can I use for car wash?'],
  },
  '/integration-guide/rydpay-apis/car-wash-client-flow': {
    keywords: [
      'car wash client flow',
      'wash flow',
      'car wash flow',
      'wash sequence',
      'car wash steps',
      'wash bay selection',
      'wash program',
      'wash code',
    ],
    questions: [
      'What is the client flow for car wash?',
      'Which API calls do I make for a car-wash transaction?',
      'How does the customer redeem the wash code at the bay?',
    ],
  },
  '/integration-guide/rydpay-apis/car-wash-demo-and-playground': {
    keywords: [
      'car wash demo',
      'car wash playground',
      'wash demo',
      'wash playground',
      'demo & playground',
      'interactive demo',
      'wash demo flow',
    ],
    questions: [
      'Where can I try out the car-wash flow interactively?',
      'Is there a car-wash demo I can play with?',
    ],
  },

  // -- rydpay APIs / Guides: EV Charging --
  '/integration-guide/rydpay-apis/guides-ev-charging/ev-insights-and-statistics': {
    keywords: [
      'ev charging',
      'ev insights',
      'ev statistics',
      'charging stats',
      'cpo',
      'emp',
      'charge points',
      'ev volumes',
      'roaming',
      'charge point operator',
      'emobility provider',
      'connector types',
    ],
    questions: [
      'What EV charging insights and statistics does ryd publish?',
      'How big is the EV charging network ryd covers?',
      'Which CPOs are part of the ryd network?',
    ],
  },
  '/integration-guide/rydpay-apis/guides-ev-charging/api-journey': {
    keywords: [
      'ev charging api journey',
      'ev api journey',
      'charging journey',
      'ev integration journey',
      'ev integration steps',
      'remote start',
      'remote stop',
      'session start',
      'session end',
      'charge session',
    ],
    questions: [
      'What is the API journey for EV charging integration?',
      'How do I integrate the EV charging APIs step by step?',
      'How do I remotely start and stop a charging session?',
    ],
  },
  '/integration-guide/rydpay-apis/guides-ev-charging/ev-pricing': {
    keywords: [
      'ev pricing',
      'charging pricing',
      'tariff',
      'kwh price',
      'time-based pricing',
      'energy-based pricing',
      'ocpi pricing',
      'ev tariff',
      'kwh tariff',
      'per minute tariff',
      'parking fee',
      'session fee',
      'tariff elements',
      'price components',
      'price restriction',
    ],
    questions: [
      'How does EV pricing work in ryd?',
      'Why do EV prices show kWh and per-minute rates?',
      'Where is the EV pricing model documented?',
      'How do I compute the total session price?',
    ],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation/ev-charging-test-scenarios': {
    keywords: [
      'ev charging test scenarios',
      'ev test scenarios',
      'ev sandbox tests',
      'ev qa',
      'ev session test',
      'remote start test',
    ],
    questions: ['Which test scenarios should I run for EV charging?'],
  },
  '/integration-guide/rydpay-apis/testing-and-simulation/ev-charging-simulated-stations': {
    keywords: [
      'ev charging simulated stations',
      'ev simulated stations',
      'ev sandbox stations',
      'ev test stations',
      'ocpi simulator',
      'simulated cpo',
      'fake charge point',
    ],
    questions: ['Which simulated stations are available for EV charging?'],
  },

  // -- rydpay APIs / Action Codes --
  '/integration-guide/rydpay-apis/action-codes': {
    keywords: [
      'action codes',
      'action code',
      'actioncode',
      'actioncodes',
      'action-code',
      'action-codes',
      'action_code',
      'action_codes',
      'action code overview',
      'pay codes',
      'pop codes',
      'error codes',
      'error code',
      'errorcode',
      'errorcodes',
      'error-code',
      'error-codes',
      'response codes',
      'response code',
      'responsecode',
      'responsecodes',
      'result code',
      'result codes',
      'resultcode',
      'resultcodes',
      'failure code',
      'failure codes',
      'failurecode',
      'failurecodes',
      'decline code',
      'decline codes',
      'rejection code',
      'rejection codes',
      'reason code',
      'reason codes',
      'outcome code',
      'outcome codes',
      'numeric code',
      'numeric codes',
      'code reference',
      'code catalogue',
      'code catalog',
      'code list',
      'code table',
      'integration errors',
      'ifsf',
      'ifsf mapping',
      'ifsf code',
      'action code categories',
      'action code domains',
      'action code areas',
      'remediation',
      'actionCodeText',
    ],
    questions: [
      'What are action codes in rydpay?',
      'Where is the action codes overview?',
      'How do PAY and POP action codes differ?',
      'How do I read an action code number?',
      'What does this error code mean?',
      'How do I look up an error code?',
      'Where is the action code list?',
      'What is the difference between an action code and an error code?',
    ],
    faqs: [
      {
        q: 'What is an action code?',
        a: 'Action codes are structured numeric codes returned by rydpay APIs that classify the outcome of an operation across the PAY and POP domains, broken down into areas. Each code has a canonical actionCodeText, an IFSF mapping where applicable, and a recommended remediation.',
      },
      {
        q: 'How is the action-code number structured?',
        a: 'The leading digit identifies the domain (1 = PAY, 7 = POP/station), the next digits encode the area, and the trailing digits identify the specific code. The Action Codes Overview lists every domain, every area, and every code with its IFSF mapping where applicable.',
      },
      {
        q: 'What is the difference between an action code and an error code?',
        a: 'An action code is not the same as an error code — an action code contains an error code. Every rydpay action code is exactly 8 digits and bundles four pieces of context: domain (digit 1 — 1 = PAY, 7 = POP/station), phase or area (digits 2–3), whether a response was received from the station or the request never reached it (digit 4), and the underlying numeric error code as the last 3 digits. For POP codes those trailing 3 digits are the IFSF industry-standard error code (e.g. 391 = "pump in use", 909 = "system malfunction"); for PAY codes they identify a payment-side error. Worked example: 70120391 = POP (7) + ReservePump phase (01) + response received (2) + IFSF error code 391 — i.e. "the station replied that the pump was already busy when ryd tried to reserve it". Always branch your integration on the full 8-digit numeric actionCode, not on the trailing 3-digit error code alone, because the same error code can mean different things in different phases. Partners who casually say "error code", "result code", "response code" or "failure code" in tickets and chats are almost always referring to a full ryd action code — look it up on the Action Codes Search or Solutions page.',
      },
    ],
  },
  '/integration-guide/rydpay-apis/action-codes/search': {
    keywords: [
      'search action codes',
      'search action code',
      'action code search',
      'actioncode search',
      'actioncodesearch',
      'find action code',
      'find actioncode',
      'lookup action code',
      'lookup actioncode',
      'code search',
      'action code finder',
      'actioncode finder',
      'action code lookup',
      'actioncode lookup',
      'partial code match',
      'search error code',
      'search error codes',
      'errorcode search',
      'errorcodesearch',
      'error code lookup',
      'errorcode lookup',
      'find error code',
      'find errorcode',
      'find errorcodes',
      'lookup error code',
      'lookup errorcode',
      'code finder',
      'numeric code search',
    ],
    questions: [
      'How do I search for an action code?',
      'How do I search for an error code?',
      'Where do I look up the meaning of code 70220909?',
      'How do I find every action code containing the word "limit"?',
      'Where can I look up an errorcode?',
    ],
  },
  '/integration-guide/rydpay-apis/action-codes/pop': {
    keywords: [
      'pop action codes',
      'pop action code',
      'pop actioncode',
      'pop actioncodes',
      'popactioncode',
      'popactioncodes',
      'pop codes',
      'pop code',
      'popcode',
      'popcodes',
      'pop-code',
      'pop-codes',
      'pop_code',
      'pop_codes',
      'pop error code',
      'pop error codes',
      'pop errorcode',
      'pop errorcodes',
      'pop domain',
      'pop area',
      'point of payment',
      'station codes',
      'station code',
      'station error code',
      'station error codes',
      '7xxxxxxx codes',
      '7xxxxxxx',
      'station-side codes',
      'forecourt codes',
      'forecourt code',
    ],
    questions: [
      'What are the POP action codes?',
      'Where do I find the POP code list?',
      'What does a 7xxxxxxx code mean?',
      'What is a station-side error code?',
    ],
  },
  '/integration-guide/rydpay-apis/action-codes/pay': {
    keywords: [
      'pay action codes',
      'pay action code',
      'pay actioncode',
      'pay actioncodes',
      'payactioncode',
      'payactioncodes',
      'pay codes',
      'pay code',
      'paycode',
      'paycodes',
      'pay-code',
      'pay-codes',
      'pay_code',
      'pay_codes',
      'pay error code',
      'pay error codes',
      'pay errorcode',
      'pay errorcodes',
      'pay domain',
      'payment codes',
      'payment code',
      'card codes',
      'card code',
      'card error code',
      'card error codes',
      'card decline code',
      'card decline codes',
      '1xxxxxxx codes',
      '1xxxxxxx',
      'card-side codes',
      'payment errors',
      'payment error',
      'payment error code',
      'payment error codes',
      'payment failure',
      'payment failure code',
      'payment decline',
      'decline reason',
    ],
    questions: [
      'What are the PAY action codes?',
      'Where is the PAY code list?',
      'What does a 1xxxxxxx code mean?',
      'What is a card decline code?',
      'What is a payment error code?',
    ],
  },
  '/integration-guide/rydpay-apis/action-codes/solutions': {
    keywords: [
      'action code solutions',
      'actioncode solutions',
      'actioncode solution',
      'action code solution',
      'error code solutions',
      'errorcode solutions',
      'errorcode solution',
      'error code solution',
      'solutions',
      'solution',
      'remediation',
      'how to fix action code',
      'how to fix actioncode',
      'how to fix error code',
      'how to fix errorcode',
      'fix action code',
      'fix actioncode',
      'fix error code',
      'fix errorcode',
      'troubleshoot action code',
      'troubleshoot actioncode',
      'troubleshoot error code',
      'troubleshoot errorcode',
      'recommended action',
      'action code remediation',
      'actioncode remediation',
      'error code remediation',
      'errorcode remediation',
      'retry guidance',
      'failure remediation',
      'failure resolution',
      'recover from action code',
      'recover from error code',
    ],
    questions: [
      'How do I fix action code X?',
      'How do I fix error code X?',
      'What is the recommended remediation for an action code?',
      'What is the recommended remediation for an error code?',
      'Where do I find solutions for failed orders?',
      'Should I retry on action code 70220909?',
      'How do I troubleshoot a failing rydpay order?',
    ],
  },

  // -- rydpay APIs / API reference --
  '/integration-guide/rydpay-apis/api-reference': {
    keywords: [
      'api reference',
      'rydpay api reference',
      'ryd.pay api reference',
      'swagger',
      'openapi',
      'endpoints',
      'reference',
      'rest reference',
      'api endpoints list',
      'http methods',
      'request schemas',
      'response schemas',
    ],
    questions: [
      'Where is the rydpay API reference?',
      'How do I see the Swagger / OpenAPI for rydpay?',
      'Which endpoints are exposed by rydpay?',
    ],
  },

  // -- rydpay APIs / Support & Resources --
  '/integration-guide/rydpay-apis/terms-and-definitions': {
    keywords: [
      'terms',
      'definitions',
      'glossary',
      'rydpay glossary',
      'terminology',
      'definitions of terms',
      'concept dictionary',
      'jargon glossary',
      'platform glossary',
      'fuel glossary',
      'ev glossary',
    ],
    questions: ['What does term X mean?', 'Where is the rydpay glossary?'],
  },
  '/integration-guide/rydpay-apis/faqs': {
    keywords: [
      'faq',
      'faqs',
      'frequently asked questions',
      'help',
      'rydpay faq',
      'integration faq',
      'questions',
      'answers',
      'common questions',
      'partner faq',
      'x-txn-initiated-by-partner',
      'user agent blocked',
      'security incident',
    ],
    questions: [
      'Where are the rydpay FAQs?',
      'Do you have answers to common integration questions?',
      'Why are my requests being blocked by user agent?',
    ],
  },

  // -- rydpay APIs / Release notes --
  '/integration-guide/rydpay-apis/release-notes-and-updates': {
    keywords: [
      'release notes',
      'changelog',
      'updates',
      'release notes and updates',
      'rydpay changelog',
      'version history',
      'whats new',
      'breaking changes',
      'deprecation notice',
      'new endpoints',
    ],
    questions: [
      'What are the latest rydpay updates?',
      'Where do I read the rydpay release notes?',
      'Are there any breaking changes coming up?',
    ],
  },

  // -- Fleet APIs --
  '/integration-guide/fleet-apis/fleet-api-introduction': {
    keywords: [
      'fleet api',
      'fleet api introduction',
      'fleet apis intro',
      'fleet integration',
      'fleet partner api',
      'fleet endpoints',
      'fleet portal api',
      'fleet api overview',
      'b2b fleet api',
    ],
    questions: [
      'What are the Fleet APIs?',
      'How do the Fleet APIs differ from rydpay APIs?',
      'When should I use Fleet APIs?',
    ],
  },
  '/integration-guide/fleet-apis/fleet-api-authentication': {
    keywords: [
      'fleet api authentication',
      'fleet auth',
      'fleet credentials',
      'fleet token',
      'fleet api key',
      'fleet oauth',
      'fleet bearer token',
      'authorization bearer',
      'same authentication as rydpay',
      'rydpay authentication comparison',
      'oauth2 client credentials',
      'jwt bearer assertion',
      'jwt assertion',
      'direct authentication',
      'delegated authentication',
      'shared oauth framework',
      'webview userData',
      'webview vehicleData',
      'showMap',
      'shopId',
      'token endpoint fleet',
      'access token',
      'api scopes',
      'POST /auth/delegated',
      'delegated jwt claims',
      'productType',
      'productSpecs',
      'orderId',
      'car wash jwt',
      'ev charging jwt',
      'places:read',
      'shops:read',
      'backward compatible',
    ],
    questions: [
      'How do I authenticate against the Fleet APIs?',
      'How do I get fleet API credentials?',
      'What is the difference between Direct and Delegated Authentication?',
      'How do I issue a webview_token from a JWT assertion?',
      'Which ryd Connect claims go into the delegated JWT for car wash or EV charging?',
      'Do I need new credentials for car wash and EV charging?',
    ],
    faqs: [
      {
        q: 'Is Fleet API authentication the same as ryd Connect authentication?',
        a: 'Yes — both use the same OAuth2 framework (JWT Bearer Assertion + Client Credentials). Existing Fleet API keys and credentials work for ryd Connect; for a ryd Connect session the delegated JWT also carries the session claims: `productType` (`GAS`, `CAR_WASH`, `EV_CHARGE`), `productSpecs`, `userData`, `vehicleData`, `redirectUrl`, `showMap`/`shopId`, and optionally `orderId` to reopen an order.',
      },
      {
        q: 'Which claims does the delegated JWT need for ryd Connect car wash or EV charging?',
        a: 'Next to `iss`, `sub`, `iat` and `exp`: `productType` (`CAR_WASH` or `EV_CHARGE`), `productSpecs` (car wash: optional `maxAmounts`; EV: optional `plugTypes` and `maxPowerKw`, no amount), `userData.userId` and `redirectUrl`. The Fleet API Authentication page shows a switchable example per product, and the Fleet API Reference v1.2.2 documents them on `POST /auth/delegated`. The token exchange itself is unchanged.',
      },
      {
        q: 'Do existing delegated JWTs keep working after the car wash and EV charging release?',
        a: 'Yes — the release is backward compatible. Payloads without `productType` are treated as fuel, the earlier fields (`vehicleData.fuelType`, top-level `authorizeAmounts`) are still accepted next to the new ones, and the token endpoints, request bodies, launch URLs and old page links are unchanged. Car wash and EV charging are opt-in through `productType`.',
      },
      {
        q: 'Is Fleet API authentication the same as rydpay API authentication?',
        a: 'No. Fleet APIs use OAuth2-based JWT access tokens obtained through Client Credentials or JWT Bearer authentication and then sent as `Authorization: Bearer <access_token>`. rydpay APIs use a static API key in the `x-txn-auth-token` header.',
      },
      {
        q: 'When should I use Direct vs. Delegated Authentication?',
        a: 'Direct (OAuth2 Client Credentials) is for server-to-server traffic where the partner backend acts on its own behalf. Delegated (JWT Bearer Assertion → webview_token) is for flows where the partner authenticates a specific end-user — typically before launching ryd Connect.',
      },
    ],
  },
  '/integration-guide/fleet-apis/fleet-api-reference': {
    keywords: [
      'fleet api reference',
      'fleet endpoints',
      'fleet swagger',
      'fleet openapi',
      'fleet api docs',
      'fleet rest reference',
      'fleet endpoint catalog',
      'fleet api spec version',
      'fleet api 1.2.3',
      'fleet api 1.2.2',
      'fleet api 1.2.1',
      'openapi version',
      'download fleet api spec',
      'POST /auth/delegated',
      'GET /v4/places',
      'vendingMachinesTypes',
      'app-login-token',
      'app_login:write',
    ],
    questions: [
      'Where is the Fleet API reference?',
      'Which Fleet API endpoints are available?',
      'How do I authenticate Fleet API requests in Swagger?',
      'Which Fleet API spec version should I use?',
      'What changed between Fleet API spec v1.2.1 and v1.2.2?',
      'What is new in Fleet API spec v1.2.3?',
    ],
    urlParameters: ['version', 'env'],
    faqs: [
      {
        q: 'What is the difference between Fleet API spec v1.2.2 and v1.2.1?',
        a: 'v1.2.2 (the default) documents the ryd Connect product claims on `POST /auth/delegated` — `productType`, `productSpecs`, `orderId` — with one example per product, a reopen example and the earlier fuel-only shape. v1.2.1 is kept unchanged. Endpoints, request bodies and responses are identical, so the change is backward compatible; switch versions on the page or with `?version=1.2.1`.',
      },
      {
        q: 'What is new in Fleet API spec v1.2.3?',
        a: 'v1.2.3 (the default) adds Login to ryd: `POST /fleets/{fleetId}/drivers/{driverId}/app-login-token` issues a single-use token that opens the ryd app signed in to one of your fleet drivers, with the restricted scope `app_login:write`. It also documents the `searchTerm` filter of `GET /fleets/{fleetId}/drivers`. Every existing endpoint is unchanged; v1.2.2 stays selectable with `?version=1.2.2`.',
      },
    ],
  },
  '/integration-guide/fleet-apis/fleet-api-app-login': {
    keywords: [
      'login to ryd',
      'app login',
      'partner app login',
      'deep link login',
      'deeplink',
      'open ryd app signed in',
      'single sign on ryd app',
      'login token',
      'app-login-token',
      'app_login:write',
      'loginHint',
      'ryd.go.link',
      'shared driver account',
      'register driver in advance',
      'driverId',
      'tokenId',
    ],
    questions: [
      'How do I open the ryd app already signed in from my app?',
      'How do I request a login token for a driver?',
      'Do I need to create the driver before the first login?',
      'Can several of my users share one ryd driver account?',
      'What link does my app open after it receives the login token?',
      'Why does a login link say it expired?',
    ],
    faqs: [
      {
        q: 'Do I need to create the driver before the first login?',
        a: 'Yes. Login to ryd never creates an account. Register the driver once with `POST /fleets/{fleetId}/drivers`, store the returned `accountId` as `driverId`, and request login tokens for that `driverId` on each tap.',
      },
      {
        q: 'Can several of my users share one ryd driver account?',
        a: 'Yes, if your use case needs it: register one driver with an email you control and request tokens for its `driverId` for each of those users. They then share one ryd account, with the same transactions, vehicles, payment methods and receipts. There is one open token per driver, so if two users tap within the token lifetime, the first link expires and that user taps again.',
      },
      {
        q: 'What does my app open after it receives the token?',
        a: '`https://ryd.go.link/login?loginHint=<token>` in production and `https://rydappstage.go.link/login?loginHint=<token>` on staging, at once and only after checking that the ryd app is installed. Never log, store or forward the token; log the `tokenId` for support.',
      },
    ],
  },

  // -- Partner Wallet --
  '/integration-guide/rydpay-apis/partner-wallet': {
    keywords: [
      'partner wallet',
      'partner wallet integration',
      'hosted wallet',
      'hosted driver wallet',
      'hosted fuel card wallet',
      'oem partner wallet',
      'oem wallet',
      'wallet',
      'fuel card wallet',
      'fuel card payment method',
      'bp aral routex',
      'routex card',
      'payment method management',
      'short-lived token',
      'short term token',
      'driver wallet token',
      'partners drivers short-term-token',
      'x-txn-auth-token',
      'driverIdAtPartner',
      'redirectUrl',
      'redirectTimeoutUrl',
      'theme override',
      'wallet theme variables',
      'wallet localization',
      'wallet locale',
      '2fa support',
      'pin support',
      'vin support',
      'vehicleId support',
      'country support',
      'mock fuel card',
      'fuel card checkout',
      'paymentMethodId',
      'partnerExtraData',
      'authorize fuel card',
      'get fuel card payment methods',
    ],
    questions: [
      'How does the partner wallet integration work?',
      'Where is the partner wallet documentation?',
      'How do I generate a hosted wallet URL for a driver?',
      'Which prerequisites are needed for hosted wallet integration?',
      'Which fuel cards are supported by the partner wallet?',
      'How do I test the partner wallet with mock fuel cards?',
      'How do I pass a selected fuel card payment method into checkout?',
      'How can partners customize hosted wallet theme and locale?',
    ],
    faqs: [
      {
        q: 'How does the hosted partner wallet flow work?',
        a: 'The partner sends the driver to a ryd-hosted wallet URL generated with a short-lived token. The driver adds a supported fuel-card payment method, then the partner fetches available payment methods and passes paymentMethodId plus partnerExtraData in the authorize call during checkout.',
      },
      {
        q: 'What prerequisites are required for partner wallet integration?',
        a: 'A partner needs a valid ryd partner account and authentication token, plus ryd-side hosted wallet settings. Optional configuration covers 2FA or PIN support, VIN or vehicleId checks, allowed countries, theme overrides and locale handling.',
      },
      {
        q: 'How do I generate a hosted wallet URL?',
        a: 'Call POST /auth/partners/drivers/{driverIdAtPartner}/short-term-token with x-txn-auth-token and body values such as redirectUrl, redirectTimeoutUrl, countryCode, locale, theme, vin and vehicleId. The returned URL is valid for 30 minutes.',
      },
      {
        q: 'How do I test partner wallet fuel cards?',
        a: 'Use the mock BP_ARAL_ROUTEX fuel-card values listed on the Partner Wallet Integration Guide. For the mock checkout flow, use pump 6 on the documented staging station ID.',
      },
      {
        q: 'How can the hosted wallet UI be customized?',
        a: 'The page documents theme support, locale support and example screenshots. Partners can request theme overrides for hosted wallet variables such as input background, text, focus stroke and error colors.',
      },
    ],
  },

  // -- ryd Connect --
  '/integration-guide/ryd-connect/getting-started/overview': {
    keywords: [
      'ryd connect',
      'connect overview',
      'ryd connect getting started',
      'webview integration',
      'connect introduction',
      'webview optimized integration',
      'mobility payment services',
      'fuel ev charging car wash',
      'native mobile apps',
      'web applications',
      'quick start',
      'car wash',
      'ev charging',
      'productType',
      'backward compatible',
      'menu structure',
      'old links',
      'supported flows',
      'integration architecture',
    ],
    questions: [
      'What is ryd Connect?',
      'How do I get started with ryd Connect?',
      'Which flows does ryd Connect support?',
      'Does ryd Connect support car wash and EV charging?',
      'Is the car wash and EV charging release backward compatible?',
      'Do my old ryd Connect documentation links still work?',
    ],
    faqs: [
      {
        q: 'Does ryd Connect support car wash and EV charging?',
        a: "Yes. Fuel (`GAS`), car wash (`CAR_WASH`) and EV charging (`EV_CHARGE`) all run through the same webview and the same authentication. Choose the flow with `productType` and send that product's settings in `productSpecs`; the Products page under Products & Payload documents each product.",
      },
      {
        q: 'Is the car wash and EV charging release backward compatible?',
        a: 'Yes — the release is backward compatible. Payloads without `productType` are treated as fuel, the earlier fields (`vehicleData.fuelType`, top-level `authorizeAmounts`) are still accepted next to the new ones, and the token endpoints, request bodies, launch URLs and old page links are unchanged. Car wash and EV charging are opt-in through `productType`.',
      },
      {
        q: 'Do my old ryd Connect documentation links still work?',
        a: 'Yes. The pages are now grouped (Getting Started, Products & Payload, Authentication Flows, App Integration, Testing & Resources) under paths like `/integration-guide/ryd-connect/app-integration/callbacks`, and every previous address — including links with a section such as `/integration-guide/ryd-connect/callbacks#error-codes` — opens the same page and section.',
      },
      {
        q: 'What is ryd Connect?',
        a: "ryd Connect is a WebView-optimised integration solution for native mobile apps and web applications, covering fuel, EV charging and car wash flows. The hosted webview handles the customer-facing payment journey so partners don't have to integrate the rydpay REST APIs directly.",
      },
    ],
  },
  '/integration-guide/ryd-connect/getting-started/integration-pathways': {
    keywords: [
      'ryd connect integration pathways',
      'connect pathways',
      'connect paths',
      'connect integration options',
      'connect pathway comparison',
      'connect decision guide',
    ],
    questions: [
      'Which ryd Connect integration pathway should I pick?',
      'How do the ryd Connect integration options compare?',
    ],
  },
  '/integration-guide/ryd-connect/authentication': {
    keywords: [
      'ryd-connect',
      'ryd connect',
      'rydconnect',
      'rydConnect',
      'ryd connect authentication',
      'connect auth',
      'authentication overview',
      'auth decision',
      'session token vs jwt',
      'one-time token',
      'shared oauth framework',
      'oauth2 client credentials',
      'jwt bearer assertion',
      'webview_token',
      'chrome custom tabs',
      'sfsafariviewcontroller',
      'reusable token',
      'ryd connect language',
      'ryd connect languages',
      'supported languages',
      'language support',
      'lang parameter',
      'lang query parameter',
      'i18n',
    ],
    questions: [
      'Which authentication flow should I pick for ryd Connect?',
      'What is the difference between session token and JWT assertion?',
      'When do I need a one-time token?',
      'Which languages are supported by ryd Connect?',
      'How do I pass the language in the ryd Connect URL?',
      'How can I specify the language in ryd-connect?',
      'How can I specify the language in ryd connect?',
      'How can I specify the language in rydConnect?',
    ],
    urlParameters: ['lang'],
    faqs: [
      {
        q: 'Why do Chrome Custom Tabs and SFSafariViewController require a one-time token?',
        a: "Both browsers run outside the host app's storage context, so a long-lived reusable token would be vulnerable to leakage. ryd Connect issues a single-use webview_token instead; the browser surface receives only that, the original credentials never leave your backend.",
      },
      {
        q: 'How do I set the ryd Connect interface language?',
        a: 'Append the `lang` query parameter to the final launch URL (for example `&lang=de`). Supported codes are `de`, `en`, `nl`, `fr`, `it`, `es`, `pt`, `da`, and `pl`. Do not put language into the JWT or session token payload.',
      },
      {
        q: 'How can I specify the language in ryd-connect?',
        a: 'Use the `lang` URL query parameter on the final ryd Connect entry URL. Example: `https://connect.rydapi-test.com/login?webview_token=YOUR_WEBVIEW_TOKEN&lang=de`. Supported values are `de`, `en`, `nl`, `fr`, `it`, `es`, `pt`, `da`, and `pl`.',
      },
    ],
  },
  '/integration-guide/ryd-connect/authentication/session-token': {
    keywords: [
      'session token',
      'session-token auth',
      'connect session token',
      'login session',
      'partner session',
      'reusable session token',
      'one-time session token',
      'server-to-server token',
      'session storage',
      'flow 1a',
      'flow 1b',
      'validation endpoint',
      'productType session token',
      'car wash session token',
      'ev charging session token',
    ],
    questions: [
      'How does the ryd Connect session token flow work?',
      'How do I issue a session token for a user?',
      'Can I reuse a session token across requests?',
      'How do I send productType and productSpecs with a session token?',
    ],
    faqs: [
      {
        q: 'How do I open car wash or EV charging with a session token?',
        a: 'Store the same session fields you would put into a JWT — `productType`, `productSpecs`, `userData`, `vehicleData`, `redirectUrl`, `showMap`/`shopId` or `orderId` — with the session token on your backend, and return them from your validation endpoint when ryd calls it. Without `productType` the session is fuel.',
      },
    ],
  },
  '/integration-guide/ryd-connect/authentication/jwt-assertion': {
    keywords: [
      'jwt assertion',
      'jwt',
      'connect jwt',
      'partner jwt',
      'signed assertion',
      'oauth jwt bearer',
      'POST /auth/delegated',
      '/auth/delegated',
      'webview_token',
      'jwt payload claims',
      'jwt header',
      'flow 2a',
      'flow 2b',
      'staging gateway',
      'enterprise auth',
      'productType claim',
      'productSpecs claim',
      'orderId claim',
      'redirectUrl claim',
    ],
    questions: [
      'How do I sign a JWT assertion for ryd Connect?',
      'Which claims are required in the JWT?',
      'Which endpoint exchanges a JWT for a webview token?',
      'What is the difference between Flow 2a and Flow 2b?',
      'Where do productType and productSpecs go in the JWT?',
    ],
    faqs: [
      {
        q: 'How do I exchange a JWT assertion for a webview token?',
        a: 'POST your signed JWT to `/auth/delegated`. The response contains a single-use webview_token; append it to the ryd Connect URL when launching the webview. JWT assertion always issues a one-time token by design.',
      },
    ],
  },
  '/integration-guide/ryd-connect/authentication/one-time-token': {
    keywords: [
      'one-time token',
      'ott',
      'one time token',
      'single use token',
      'one-time-token security',
      'webview_token security',
      'token leak prevention',
      'ott security',
    ],
    questions: [
      'How does the one-time token flow work?',
      'When should I use one-time tokens vs JWT assertions?',
      'Why does the token become invalid after a single use?',
    ],
  },
  '/integration-guide/ryd-connect/getting-started/webview-approaches': {
    keywords: [
      'webview',
      'webview approaches',
      'iframe',
      'webview integration',
      'native webview',
      'react native webview',
      'android webview',
      'ios wkwebview',
      'wkwebview',
      'embedded webview',
      'chrome custom tabs',
      'sfsafariviewcontroller',
      'in-app browser',
    ],
    questions: [
      'How do I embed ryd Connect in a webview?',
      'Which webview approach works best for iOS/Android/React Native?',
      'Embedded WebView vs Chrome Custom Tabs — which one should I pick?',
    ],
  },
  '/integration-guide/ryd-connect/app-integration/callbacks': {
    keywords: [
      'callbacks',
      'connect callbacks',
      'events',
      'callbacks and events',
      'js postmessage',
      'event bridge',
      'webview callback',
      'postmessage events',
      'return urls',
      'redirect callback',
      'native bridge',
      'callback overview',
      'error codes',
      'error code',
      'error screens',
      'error screen',
      'frontend errors',
      'user errors',
      'status error code',
      'NO_TOKEN',
      'UNAUTHORIZED',
      'UNKNOWN_ERROR',
      'NO_PUMPS',
      'STATION_OFFLINE',
      'ORDER_CREATION_FAILED',
      'FUELING_POSTPAY',
      'ORDER_AUTHORIZATION_FAILED',
      'UNKNOWN_SHOP_ID',
      'NO_PM_SUPPORTED_SHOP',
      'INVALID_PARTNER_DATA',
      'UNSUPPORTED_SHOP_TYPE',
      'ORDER_NOT_FOUND',
      'error_code',
      'order_id',
      'status=in_progress',
      'in_progress',
      'redirect parameters',
      'webhooks',
      'order webhook',
      'ORDER_EVENT',
      'INVOICE_EVENT',
      'FLEET_EVENT',
      'fleet status events',
      'fleet events',
      'fleet onboarding events',
      'eventCategory',
      'session timeout',
      'order timeout',
      'order cleanup',
      'per-state cleanup',
      'cleanup sweep',
      'pump timeout',
      'product timeout',
      'PRODUCT_PENDING',
      'READY_FOR_INVOICE',
      'token timeout',
      'token expiry',
      'expires_in',
      'how long to start fuelling',
      'how long to finish fuelling',
      'single-use token',
      'one-time launch token',
      'token was already used',
      '401 token',
      'auto cancelled',
      'automatic cancellation',
      'order auto cancel',
    ],
    questions: [
      'Which events does ryd Connect emit?',
      'How do I receive callbacks from the ryd Connect webview?',
      'When should I use PostMessage events vs webhooks vs return URLs?',
      'What error codes can the ryd Connect webview return?',
      'Which parameters does the redirect back to my app carry?',
      'What does status=in_progress mean?',
      'Which webhook events are available — are fleet events supported?',
      'What does the error code NO_TOKEN / UNAUTHORIZED / STATION_OFFLINE mean?',
      'Which error screens does the user see in ryd Connect?',
      'How long does a user have to start and finish fuelling after the webview opens?',
      'Why did my ryd Connect session expire / fail to call back?',
      'Why was my order automatically cancelled?',
      'Why does reusing the launch link return 401 "Token was already used"?',
    ],
    faqs: [
      {
        q: 'What are the three callback mechanisms in ryd Connect?',
        a: 'PostMessage events (real-time, client-side, embedded WebView; fuel only today), webhooks (server-side and the reliable record for every product — amounts, the car wash ticket code, how an EV session ended), and the redirect to your `redirectUrl` with `status`, `order_id`, `productType` and `error_code`. Most integrations combine all three.',
      },
      {
        q: 'What error codes can the ryd Connect webview return, and what do they mean?',
        a: 'The redirect to your `redirectUrl` carries `status` (`success`, `in_progress`, `error`, `cancel`), `order_id`, `productType` and, with `status=error`, an `error_code`. Codes: UNSUPPORTED_SHOP_TYPE (the station behind shopId is not of the sent productType), STATION_OFFLINE, ORDER_NOT_FOUND (reopening an order that does not exist or belongs to another driver or product), NO_TOKEN (opened without a webview_token), UNAUTHORIZED (session expired), UNKNOWN_ERROR (unmapped fallback), INVALID_PARTNER_DATA (malformed partner data, e.g. productSpecs.fuelTypes or authorizeAmounts missing for fuel), UNKNOWN_SHOP_ID and NO_PM_SUPPORTED_SHOP. ORDER_CREATION_FAILED, FUELING_POSTPAY and ORDER_AUTHORIZATION_FAILED are recovered inside the webview without a callback. The Callbacks page shows the screen for each code.',
      },
      {
        q: 'What does status=in_progress mean in the ryd Connect redirect?',
        a: 'The driver left the webview while an EV charging session was still running — the session continues on the charger. Show a "charging in progress" entry in your app and reopen the order with `orderId` to let the driver stop it; the order webhook reports how and when the session ended.',
      },
      {
        q: 'Which webhook events does ryd send — are fleet events supported?',
        a: 'Three topics, each with an `eventCategory` added to every delivery: order events (`ORDER_EVENT` — CREATED, PAYMENT_AUTHORIZED, COMPLETED, CANCELLED, FAILED and more, for fuel, car wash and EV charging), invoice events (`INVOICE_EVENT` — RECEIPT_GENERATED, INVOICE_GENERATED) and fleet status events (`FLEET_EVENT` — PENDING, ACTIVE, SUSPENDED, TERMINATED, per fleet). Fleet status events are live; the earlier "fleet onboarding events" placeholders never existed. Configure them in Webhook Management.',
      },
      {
        q: 'How long does a user have to start and finish a fuelling after the webview opens?',
        a: 'There are two different timing models. To START: the token you receive is valid for the time given in its `expires_in` field (currently 3600 s = 1 hour in production); the driver must create an order (select a station and pump) before it expires, otherwise they simply re-authenticate. AFTER the order is created there is no single order timeout — two safety nets apply instead: a pump/charger timeout of about 7 minutes (if the pump does not respond the order is flagged as PROBLEM), and a background cleanup sweep that runs every 15 minutes against per-state thresholds (early states ~10 min, PRODUCT_PENDING ~25 min, READY_FOR_INVOICE ~24 h), so the real-world limit is the threshold plus up to one sweep. Open the session close to actual fuelling rather than far ahead.',
      },
      {
        q: 'Why was my ryd Connect order automatically cancelled (or flagged as a problem)?',
        a: 'There is no single one-hour order timeout. Each order state has its own cleanup threshold (early states ~10 min, PRODUCT_PENDING ~25 min, READY_FOR_INVOICE ~24 h) and a background job sweeps every 15 minutes, so a stuck order is resolved after its threshold plus up to one sweep interval. Separately, a pump/charger timeout of about 7 minutes flags non-responsive fuelling/charging as PROBLEM. When a threshold is exceeded the order is moved to FAILED, REJECTED, CANCELLED or PROBLEM and you receive the matching callback — so interpret a late CANCELLED/PROBLEM against the order’s actual state, not a fixed one-hour rule. (EV charging sessions in later steps are intentionally left to run long.)',
      },
      {
        q: 'Why does reusing the ryd Connect launch link return 401 "Token was already used"?',
        a: 'The webview_token that opens ryd Connect is single-use: it is consumed the first time the session is launched, regardless of how the order ends. Reusing the same launch link is rejected with 401 "Token was already used". Request a fresh launch token for every fuelling and never cache or reuse one. The access_token used for API calls is different and stays reusable until it expires.',
      },
    ],
  },
  '/integration-guide/ryd-connect/app-integration/theming': {
    keywords: [
      'theming',
      'connect theming',
      'partner theming',
      'branding',
      'colors',
      'logo',
      'custom styles',
      'white label',
      'theme tokens',
      'css variables theme',
      'partner branding',
      'theme palette',
    ],
    questions: [
      'How do I theme ryd Connect with our brand colors?',
      'Where do I configure my partner branding for ryd Connect?',
    ],
  },
  '/integration-guide/ryd-connect/testing/guide': {
    keywords: [
      'connect testing',
      'ryd connect testing',
      'test environment',
      'sandbox',
      'integration tests connect',
      'staging connect',
      'connect sandbox',
      'test users',
      'test cards',
      'demo playground',
      'webview launcher',
      'playground apps',
      'verification checklist',
      'test car wash',
      'test ev charging',
    ],
    questions: [
      'How do I test my ryd Connect integration?',
      'Where is the ryd Connect sandbox?',
      'Which test users / cards do I use?',
      'Where is the ryd Connect demo playground?',
      'How do I test car wash and EV charging in ryd Connect?',
    ],
    faqs: [
      {
        q: 'Where can I try ryd Connect end to end?',
        a: 'Open the Demo Playground at https://partners-demo-frontend.web.app/partners/demo/webview-launcher. It lists every flow (1a, 1b, 2a, 2b and Skip the Map) and runs a session on staging with the demo credentials or your own staging keys. Use the simulated test stations in Munich for fuel, car wash and EV charging.',
      },
    ],
  },
  '/integration-guide/ryd-connect/testing/simulated-stations': {
    keywords: [
      'connect simulated stations',
      'ryd connect sandbox stations',
      'simulated stations connect',
      'test stations connect',
      'connect sandbox forecourt',
      'DE Automatic Noop',
      'DE Manual Noop',
      'DE Wash Noop 02',
      'DE EV Noop All Successful',
      'car wash test station',
      'ev charging test station',
      'pump 1',
      'pump 4',
      'pump 10',
      'munich location',
      'geolocation spoof',
    ],
    questions: [
      'Which simulated stations are available in ryd Connect?',
      'Which car wash and EV charging test stations can I use?',
      'Which pump simulates an error or an unclear status?',
      'How do I spoof my location to Munich?',
    ],
    faqs: [
      {
        q: 'Which test stations does ryd Connect offer for fuel, car wash and EV charging?',
        a: 'All in Munich: fuel DE Automatic Noop (`60c9bfe0f2d000d67561f172`, prepay) and DE Manual Noop (`60c9bfe0f2d000d67561f173`, postpay); car wash DE Wash Noop 02 (`3bb31453-6d66-4772-8adc-51a7ab0b655f`); EV charging DE EV Noop All Successful (`b989ce59-d976-411d-ba30-0d2e1f90baac`). Spoof the browser location to 48.140081088, 11.53804828, or open a station directly with `showMap: false` and its shopId.',
      },
      {
        q: 'Which pumps simulate which fuel outcome?',
        a: 'Pump 1 completes successfully, Pump 4 fails (station does not work) and Pump 10 ends in an unclear final status (PROBLEM) — Pump 10 only on DE Automatic Noop. The car wash and EV charging test sites have no pump scenarios.',
      },
    ],
  },
  '/integration-guide/ryd-connect/payload/skip-the-map': {
    keywords: [
      'skip the map',
      'skip map',
      'no-map flow',
      'direct station',
      'station preselect',
      'showMap false',
      'shopId',
      'POST /auth/direct',
      '/auth/direct',
      'GET /v4/places',
      '/v4/places',
      'cross-path feature',
      'direct pump selection',
      'location detection',
      'car wash shopId',
      'ev charging shopId',
      'vendingMachinesTypes',
      'UNSUPPORTED_SHOP_TYPE',
    ],
    questions: [
      'How do I preselect a station and skip the map in ryd Connect?',
      'Which fields do I add to the JWT to skip the map?',
      'How do I find the right shopId?',
    ],
    faqs: [
      {
        q: 'How do I make ryd Connect skip the map and open a station directly?',
        a: "Add `showMap: false` and the target `shopId` to your JWT assertion payload (or to the data stored with your session token). The driver lands straight on pump selection (fuel), programme selection (car wash) or plug selection (EV charging) at that station. The station's type must match `productType`, otherwise the redirect reports `UNSUPPORTED_SHOP_TYPE`. Works for both Session Token and JWT Assertion flows.",
      },
      {
        q: 'How do I find the shopId for a station?',
        a: 'Authenticate via `POST /auth/direct` (scope `places:read`) to obtain an access token, then call `GET /v4/places` filtered with `vendingMachinesTypes[]=GAS`, `CAR_WASH` or `EV_CHARGE` and pick the matching place. Each place exposes the shopId you pass into the Skip the Map flow.',
      },
    ],
  },
  '/integration-guide/ryd-connect/payload/products': {
    keywords: [
      'car wash',
      'carwash',
      'wash ticket',
      'ticket code',
      'ev charging',
      'ev charge',
      'charging session',
      'productType',
      'productSpecs',
      'CAR_WASH',
      'EV_CHARGE',
      'GAS',
      'maxAmounts',
      'plugTypes',
      'maxPowerKw',
      'fuelTypes',
      'authorizeAmounts',
      'orderId',
      'reopen order',
      'ORDER_NOT_FOUND',
      'UNSUPPORTED_SHOP_TYPE',
      'error_code',
      'in_progress',
      'redirect status',
      'resume charging',
      'demo playground',
      'backward compatible',
      'migration',
      'vehicleData.fuelType',
      'fuel types',
      'wash programme',
      'plug types',
      'EV_CCS',
      'EV_TYPE_2',
    ],
    questions: [
      'How do I open ryd Connect for a car wash?',
      'How do I open ryd Connect for EV charging?',
      'What goes into productSpecs?',
      'What does status=in_progress mean?',
      'How do I reopen an order with orderId?',
      'How do I migrate vehicleData.fuelType to productSpecs?',
      'Will my fuel integration keep working?',
      'Do I have to send an amount for EV charging?',
    ],
    faqs: [
      {
        q: 'How do I open ryd Connect for car wash or EV charging?',
        a: 'Add `productType` (`CAR_WASH` or `EV_CHARGE`) to your JWT payload and send only that product\'s settings in `productSpecs`: `maxAmounts` for a car wash, `plugTypes` and `maxPowerKw` for EV. Fuel uses `productType: "GAS"` with `productSpecs.fuelTypes` and `productSpecs.authorizeAmounts`. Payloads without `productType` are treated as fuel.',
      },
      {
        q: 'How do I bring a driver back to an order, for example a running charging session?',
        a: 'Send the same payload again with `orderId` set to the id from the order webhook. ryd Connect skips station and product selection and opens the screen that matches the order: the live status while it runs, the billing screen, or the receipt (with the wash ticket code). The order must belong to the same `userData.userId` and `productType`; otherwise the driver sees `ORDER_NOT_FOUND`. Reopening never creates a second order or charges again.',
      },
      {
        q: 'Will my existing fuel integration keep working?',
        a: 'Yes — the release is backward compatible. Payloads without `productType` are treated as fuel, the earlier fields (`vehicleData.fuelType`, top-level `authorizeAmounts`) are still accepted next to the new ones, and the token endpoints, request bodies, launch URLs and old page links are unchanged. Car wash and EV charging are opt-in through `productType`.',
      },
      {
        q: 'Do I send an amount for EV charging?',
        a: "No. EV charging takes no amount — the hold on the payment method comes from the fleet account's EV settings. `productSpecs` for EV is optional: `plugTypes` (e.g. `EV_CCS`, `EV_TYPE_2`) lists matching plugs first, and `maxPowerKw` orders them by the vehicle's highest charging power.",
      },
    ],
  },
  '/integration-guide/ryd-connect/payload/partner-extra-data': {
    keywords: [
      'partner extra data',
      'partnerextradata',
      'body.partnerExtraData',
      'jwt extra claims',
      'extra jwt claims',
      'ryd connect extra metadata',
      'webhook partner extra data',
      'userData mapping',
      'vehicleData mapping',
      'partnerOrderId',
      'paymentMethod',
      'locationData',
      'metadata claim',
      'vehicleId',
      'pin redaction',
      'jwt to webhook mapping',
    ],
    questions: [
      'How do I pass partner extra data into ryd Connect?',
      'Which JWT claims end up in body.partnerExtraData?',
      'Where is the partner extra data mapping documented?',
      'Should I send a partnerExtraData field in the JWT?',
    ],
    faqs: [
      {
        q: 'How do I send partner extra data to ryd Connect?',
        a: 'Do not send a dedicated `partnerExtraData` object in the JWT. Instead, add the relevant values as the documented JWT claims: reuse fields in `userData` and `vehicleData` where they belong, and add optional partner-specific top-level claims such as `partnerOrderId`, `paymentMethod`, `locationData` or `metadata`. ryd maps those values onto the order and exposes them in webhook payloads under `body.partnerExtraData`.',
      },
      {
        q: 'Where does partner extra data appear after the session starts?',
        a: 'ryd stores the mapped values on the created order and includes them in order-related webhook payloads at `body.partnerExtraData`. Sensitive values like PINs follow the page-specific redaction rules documented on the Partner Extra Data page.',
      },
    ],
  },
  '/integration-guide/ryd-connect/testing/developer-resources': {
    keywords: [
      'connect developer resources',
      'developer resources',
      'sdk samples',
      'connect samples',
      'cookbooks',
      'code examples connect',
      'sample apps connect',
      'integration cookbook',
      'postman collection',
      'ryd connect postman',
      'collection variables',
      'PRODUCT_TYPE',
      'ORDER_ID',
      'payload examples',
      'launch url',
      'launch urls per flow',
      'redirect back to your app',
    ],
    questions: [
      'Where are the developer resources for ryd Connect?',
      'Are there sample apps I can clone?',
      'Where do I download the ryd Connect Postman collection?',
      'How do I test car wash or EV charging with Postman?',
      'Which launch URL opens ryd Connect for each flow?',
    ],
    faqs: [
      {
        q: 'How do I run a car wash or EV charging session with the Postman collection?',
        a: 'Download the collection on Developer Resources and import it, set `PRODUCT_TYPE` to `CAR_WASH` or `EV_CHARGE` (plus `MAX_AMOUNT`, or `PLUG_TYPES` and `MAX_POWER_KW`), set the variables of your flow, then run one folder top to bottom. The last request prints the launch URL in the Postman Console. Set `ORDER_ID` to reopen an existing order instead.',
      },
    ],
  },

  // -- Webapp Integration (Legacy) --
  '/integration-guide/webapp-integration/introduction': {
    keywords: [
      'webapp integration',
      'webapp introduction',
      'legacy webapp',
      'topup webapp',
      'deprecated webapp',
      'legacy integration',
      'webapp deprecation',
    ],
    questions: [
      'What is the legacy webapp integration?',
      'Should I still use the webapp integration or move to ryd Connect?',
    ],
  },
  '/integration-guide/webapp-integration/getting-started': {
    keywords: [
      'webapp getting started',
      'topup getting started',
      'webapp quickstart',
      'legacy quickstart',
      'webapp setup',
    ],
    questions: ['How do I get started with the legacy webapp integration?'],
  },
  '/integration-guide/webapp-integration/topup-flow-steps': {
    keywords: [
      'topup',
      'top up',
      'topup flow',
      'topup steps',
      'wallet topup',
      'recharge',
      'aufladen',
      'topup webapp',
      'topup sequence',
    ],
    questions: ['What are the steps of the topup flow?', 'How does the legacy topup flow work?'],
  },
  '/integration-guide/webapp-integration/testing': {
    keywords: [
      'webapp testing',
      'topup testing',
      'legacy webapp testing',
      'webapp support',
      'webapp sandbox',
    ],
    questions: ['How do I test the legacy webapp integration?'],
  },

  // -- Registration Deeplink --
  '/integration-guide/deeplinking-registration/getting-started': {
    keywords: [
      'registration deeplink',
      'deeplink getting started',
      'signup deeplink',
      'registration link',
      'partner deeplink registration',
      'registration onboarding',
      'pre-fill registration',
      'invite link',
    ],
    questions: [
      'How do I create a registration deeplink for new users?',
      'Where is the registration-deeplink getting-started page?',
      'Can I pre-fill user details in a registration deeplink?',
    ],
  },
  '/integration-guide/deeplinking-registration/api-reference': {
    keywords: [
      'registration deeplink api',
      'deeplink api reference',
      'registration api',
      'signup api',
      'deeplink endpoints',
      'deeplink parameters',
    ],
    questions: ['Where is the registration-deeplink API reference?'],
  },

  // -- Android Automotive OS (in-car app) --
  '/integration-guide/android-automotive/getting-started/overview': {
    keywords: [
      'android automotive',
      'android automotive os',
      'aaos',
      'in-car app',
      'in car app',
      'car app',
      'ryd fuel & charge',
      'ryd fuel and charge',
      'infotainment',
      'oem app store',
      'in-car store',
      'harman ignite',
      'appning',
      'appning by forvia',
      'google built-in',
      'google built in',
      'play store in the car',
      'vehicle manufacturer',
      'oem integration',
      'integration levels',
      'level 2 integration',
      'automotive overview',
      'fahrzeug app',
      'auto app',
    ],
    questions: [
      'What is the ryd Android Automotive OS app?',
      'Which in-car stores list the ryd app?',
      'Do I need to integrate an SDK or API for the in-car app?',
      'Which products does the in-car app support?',
      'What are the integration levels for the in-car app?',
    ],
    faqs: [
      {
        q: 'What is the ryd Android Automotive OS app?',
        a: "ryd fuel & charge is ryd's own app for Android Automotive OS, the Android that runs in the vehicle's infotainment unit. Drivers pay for fuel, EV charging and car wash from the car screen; ryd builds, publishes and operates the app and partners approve it in their in-car store.",
      },
      {
        q: 'Which in-car stores list the ryd app?',
        a: 'The native app is live in the Harman Ignite store and in Appning by Forvia. Google built-in vehicles require a template-based version, which is planned. In which brands and markets the app is enabled is agreed per manufacturer; your account manager has the current list.',
      },
      {
        q: 'Do I need to integrate an SDK or API for the in-car app?',
        a: 'No. There is nothing to integrate: manufacturers approve the app in their store and grant vehicle data access, networks keep their station data correct, fleets onboard their drivers. In-car orders arrive in the same webhooks, reports and Fleet APIs as every other channel.',
      },
    ],
  },
  '/integration-guide/android-automotive/getting-started/platform-basics': {
    keywords: [
      'android automotive platform basics',
      'android automotive vs android auto',
      'android auto',
      'apple carplay',
      'google automotive services',
      'gas',
      'car app library',
      'template app',
      'native app',
      'hybrid app',
      'driver distraction',
      'distraction optimised',
      'distraction optimized',
      'ux restrictions',
      'car api',
      'vehicle properties',
      'rotary controller',
      'automotive glossary',
    ],
    questions: [
      'What is the difference between Android Automotive OS and Android Auto?',
      'What is the difference between a native and a template car app?',
      'What are UX restrictions in Android Automotive OS?',
      'Which vehicle data can an Android Automotive app read?',
    ],
    faqs: [
      {
        q: 'What is the difference between Android Automotive OS and Android Auto?',
        a: 'Android Automotive OS is an operating system built into the vehicle: apps are installed in the car and can read vehicle data. Android Auto is a phone projection: apps run on the phone and only templates are shown on the car screen. The ryd in-car app is an Android Automotive OS app; Android Auto and CarPlay are covered by the ryd phone app.',
      },
      {
        q: 'What is the difference between a native and a template car app?',
        a: 'A native app draws every screen itself and must enforce the driving restrictions; it is accepted by Harman Ignite and Appning by Forvia. A template app provides data that the vehicle host renders with fixed templates (five-step task limit, no web views); Google requires templates for point-of-interest apps in Google built-in vehicles.',
      },
    ],
  },
  '/integration-guide/android-automotive/getting-started/integration-pathways': {
    keywords: [
      'android automotive integration pathways',
      'in-car app pathways',
      'oem pathway',
      'vehicle manufacturer pathway',
      'in-car store operator',
      'google built-in pathway',
      'fuel network in-car',
      'charging network in-car',
      'car wash network in-car',
      'fleet in-car app',
      'fleet driver car app',
      'own automotive app',
      'ryd connect in automotive app',
      'what exists today',
      'not included',
    ],
    questions: [
      'How do I get the ryd app into my vehicles?',
      'Can fleet drivers use the ryd in-car app?',
      'Do my stations appear in the in-car app automatically?',
      'Can my own automotive app embed ryd Connect?',
    ],
    faqs: [
      {
        q: 'Can fleet drivers use the ryd in-car app?',
        a: 'Yes. A fleet driver signs in with the ryd account linked to the fleet by scanning the QR code with the phone. The fleet payment method, spending limits and allowed products apply in the car, and the orders reach the fleet reports, webhooks and Fleet APIs like every other order of that driver. In a shared car the driver logs out before handing over the keys.',
      },
      {
        q: 'Do my stations appear in the in-car app automatically?',
        a: 'Yes. Stations connected to ryd appear in the in-car app with the same data as in the ryd phone app. Correct coordinates, pump identifiers, opening hours and prices matter more in the car, because the 50 m parking match and the 100 m in-range rule depend on them.',
      },
      {
        q: 'Can my own automotive app embed ryd Connect or open the ryd app?',
        a: 'Not today. ryd does not publish an integration for third-party automotive apps. A hand-over to the ryd app and ryd Connect inside a native automotive app are explored with interested partners, but neither has a published contract, a store acceptance or a security review yet. Talk to your account manager first.',
      },
    ],
  },
  '/integration-guide/android-automotive/in-car/driver-journey': {
    keywords: [
      'android automotive driver journey',
      'in-car app screenshots',
      'in-car screens',
      'welcome screen',
      'qr code login',
      'qr login car',
      'login in the car',
      'registration in the car',
      'payment method in the car',
      'add credit card in car',
      'wallet in the car',
      'in-car filter',
      'fuel grade filter',
      'plug type filter',
      'station list map car',
      'station search car',
      'station details car',
      'refuelling in the car',
      'prepay car',
      'postpay car',
      'ev charging in the car',
      'car wash in the car',
      'wash activation code',
      'history in the car',
      'ongoing orders car',
    ],
    questions: [
      'How does a driver sign in to the ryd in-car app?',
      'Which payment methods can be added in the car?',
      'How does refuelling work in the in-car app?',
      'How does EV charging work in the in-car app?',
      'How does a car wash work in the in-car app?',
      'Why does the map not open before a payment method exists?',
    ],
    faqs: [
      {
        q: 'How does a driver sign in to the ryd in-car app?',
        a: 'The car shows a QR code; the driver scans it with the phone, confirms there, and the car receives the session encrypted for this vehicle. An in-car e-mail and password form with registration remains available. Existing ryd accounts and fleet accounts work; there is no guest use in the car.',
      },
      {
        q: 'Which payment methods can be added in the car?',
        a: 'Only credit cards can be added in the car: in a secure page of the payment provider including strong customer authentication, or on the phone after scanning a QR code. Methods already on the account, including fleet postpay, are payable and one can be set as preferred.',
      },
      {
        q: 'How does refuelling work in the in-car app?',
        a: 'Within 100 m of a station the flow starts directly. Postpay stations: choose the pump, fuel, confirm the reported amount, pay. Prepay stations: choose the pump, reserve a maximum amount (25 to 1,500 euros, default remembered), fuel, and only the fuelled amount is charged. The receipt shows date, pump, grade, litres and unit price.',
      },
    ],
  },
  '/integration-guide/android-automotive/in-car/vehicle-integration': {
    keywords: [
      'android automotive vehicle integration',
      'vehicle signals',
      'vehicle data in-car app',
      'car api properties',
      'info_make',
      'info_fuel_type',
      'info_ev_connector_type',
      'car_info permission',
      'car_powertrain permission',
      'car-parked trigger',
      'car parked notification',
      'parking broadcast',
      'parking event',
      'geofencing car',
      'in-car notification',
      'driving restrictions',
      'in range 100 m',
      'parking match 50 m',
      'navigation hand-off',
      'drive to station',
      'offline in the car',
    ],
    questions: [
      'Which vehicle data does the ryd in-car app use?',
      'How does the car-parked notification work?',
      'What changes in the in-car app while driving?',
      'When does the in-car app start the payment automatically?',
      'What does the manufacturer have to approve for the in-car app?',
    ],
    faqs: [
      {
        q: 'Which vehicle data does the ryd in-car app use?',
        a: 'Make, model and model year, and the fuel or battery capability with plug types, read once at start with the car info permission the manufacturer grants. Make, model and year are sent with the app’s requests to the ryd platform; the capability picks fuel or charging as the first product. Live levels such as fuel level, range and odometer are not read, and the permissions for them are not requested.',
      },
      {
        q: 'How does the car-parked notification work?',
        a: 'In-car stores that broadcast a parking event hand the position to the app. A background job finds the nearest online ryd station within 50 m for the driver’s product and answers with the notification title, message, icon and the target that opens the app at that station. The store shows the notification; a tap opens the payment flow. "Parking ended" cancels a pending match.',
      },
      {
        q: 'When does the in-car app start the payment automatically?',
        a: 'When exactly one fuel station or charging site is within 100 m of the vehicle and no order is running; a running order always takes priority. A car wash is always treated as in range. Selecting a station from the list within 100 m also starts the flow directly; further away a fuel station opens its details screen.',
      },
    ],
  },
  '/integration-guide/android-automotive/in-car/app-variants-and-stores': {
    keywords: [
      'android automotive app variants',
      'full native app',
      'hybrid automotive app',
      'full template app',
      'car app library build',
      'store acceptance',
      'harman ignite acceptance',
      'appning acceptance',
      'google built-in template',
      'play store car',
      'ryd fuel & charge app id',
      'min sdk automotive',
      'car api level',
      'automotive languages',
      'partner build',
    ],
    questions: [
      'Which build of the in-car app is in production?',
      'Which store accepts native automotive apps and which requires templates?',
      'Which Android version does the in-car app require?',
    ],
    faqs: [
      {
        q: 'Which build of the in-car app is in production?',
        a: 'Only the full native build ships. A hybrid build (templates for browsing, native screens for payment) is a backup design that is not published, and a full template build for Google built-in vehicles is planned; its in-car payment authentication is a design assumption until the payment provider and the store accept an approach.',
      },
      {
        q: 'Which Android version does the in-car app require?',
        a: 'Android 10 or newer (API level 29) with car API level 1 and automotive hardware. Staging and production builds have separate app ids, so both can be installed on one unit.',
      },
    ],
  },
  '/integration-guide/android-automotive/launch/best-practices': {
    keywords: [
      'android automotive best practices',
      'in-car app best practices',
      'driver distraction best practices',
      'glanceable ui',
      'five steps task limit',
      'rotary controller focus',
      'qr login best practice',
      'strong customer authentication car',
      'sca in the car',
      'amount reservation',
      'parking trigger best practice',
      'offline handling car',
      'order recovery',
      'vehicle data minimisation',
      'store review',
      'release management automotive',
      'crash reporting automotive',
      'remote configuration',
    ],
    questions: [
      'What are the best practices for an in-car payment app?',
      'How should an automotive app handle strong customer authentication?',
      'How does the ryd app avoid false parking notifications?',
    ],
    faqs: [
      {
        q: 'How should an automotive app handle strong customer authentication?',
        a: 'Keep card entry and the authentication challenge inside the payment provider’s secure page, loaded in a web view only while parked, and offer the phone as the alternative through a QR code. Template apps have no web view, which is why the Google built-in version relies on the phone for card setup.',
      },
    ],
  },
  '/integration-guide/android-automotive/launch/testing': {
    keywords: [
      'android automotive testing',
      'in-car app testing',
      'automotive checklist',
      'driving safety checklist',
      'go-live checklist automotive',
      'staging build automotive',
      'automotive emulator',
      'android studio automotive image',
      'simulated stations automotive',
      'munich test stations',
      'location spoofing emulator',
      'geo fix',
      'parking broadcast adb',
      'test parking trigger',
      'store submission',
    ],
    questions: [
      'How do I test the ryd in-car app?',
      'How do I get the staging build of the in-car app?',
      'Which simulated stations work with the in-car app?',
      'How do I simulate the car-parked trigger?',
      'What is on the driving-safety checklist?',
    ],
    faqs: [
      {
        q: 'How do I test the ryd in-car app?',
        a: 'Ask your account manager for the staging build, run it on an Android Automotive emulator image or a test unit, move the emulated vehicle to Munich (48.140081, 11.538048) and use the simulated stations: DE Automatic Noop 60c9bfe0f2d000d67561f172 (prepay fuel; pump 1 success, 4 error, 10 unclear), DE Manual Noop 60c9bfe0f2d000d67561f173 (postpay fuel), DE Wash Noop 02 3bb31453-6d66-4772-8adc-51a7ab0b655f and DE EV Noop All Successful b989ce59-d976-411d-ba30-0d2e1f90baac. Then run the driving-safety checklist.',
      },
      {
        q: 'How do I simulate the car-parked trigger?',
        a: 'On a debug build, send the store’s parking broadcast with adb, passing latitude, longitude and the sender package as string extras; ryd provides the action names of your store. Within 50 m of an online simulated station the app answers with the notification content, visible in the device log.',
      },
    ],
  },
  '/integration-guide/android-automotive/launch/resources-and-faq': {
    keywords: [
      'android automotive resources',
      'android automotive faq',
      'android for cars documentation',
      'car app library documentation',
      'car app quality guidelines',
      'design for driving',
      'vehicle property ids',
      'ux restrictions reference',
      'harman ignite developer',
      'appning developer',
      'distribute to cars',
      'related ryd pages',
      'account manager automotive',
    ],
    questions: [
      'Where is the official Android Automotive documentation?',
      'Who do I contact about the in-car app?',
      'Where do in-car orders show up for partners?',
      'Is car wash available in the in-car app?',
    ],
    faqs: [
      {
        q: 'Where do in-car orders show up for partners?',
        a: 'In the same webhooks, reports and Fleet APIs as every other order of the signed-in driver. The app sends the vehicle’s make, model and year to the ryd platform with its requests; whether that appears in your payloads is confirmed per integration with your account manager.',
      },
      {
        q: 'Is car wash available in the in-car app?',
        a: 'Yes. The driver buys a programme in the car and gets the activation code on the receipt; the code stays available in the history, in the ryd phone app and on the invoice.',
      },
      {
        q: 'Who do I contact about the in-car app?',
        a: 'Your ryd account manager: for the staging build, store contacts, the availability per manufacturer and the data approval. Store developer portals are not public.',
      },
    ],
  },

  // -- SDK --
  '/integration-guide/rydsdk/introduction': {
    keywords: [
      'sdk',
      'rydsdk',
      'ryd sdk',
      'sdk introduction',
      'sdk benefits',
      'native sdk',
      'mobile sdk',
      'ios sdk',
      'android sdk',
      'sdk overview',
      'sdk architecture',
    ],
    questions: [
      'What does the ryd SDK do?',
      'When should I use the SDK vs the REST APIs?',
      'Which platforms does the ryd SDK target?',
    ],
  },
  '/integration-guide/rydsdk/getting-started/installation': {
    keywords: [
      'sdk installation',
      'rydsdk install',
      'sdk getting started',
      'sdk available on demand',
      'sdk setup',
      'sdk gradle',
      'sdk cocoapods',
      'sdk dependency',
      'sdk download',
    ],
    questions: [
      'How do I install the ryd SDK?',
      'Where do I get the SDK package?',
      'How do I add the ryd SDK to my Gradle / CocoaPods project?',
    ],
  },
};
